import { useMemo, useEffect, useRef } from "react";
import useStableCallback from "src/hooks/useStableCallback";
import { useQuery } from "@apollo/client/react";
import { useOrgChartContext } from "src/components/OrgChart/context/OrgChartContext";
import { useDateSettings } from "src/contexts/global/ApolloSettingsContext";
import { getTeamAggregations } from "src/pages/TeamDetailPage/tabs/TeamInsightsTab/queries.graphql";
import {
  calculateLayersMetric,
  calculateSpanMetric,
  calculateManagerToICRatioMetric,
  calculateManagementOverheadMetric,
  calculateHeadcountReductionMetric,
  selectLensView,
  selectPositionView,
  type HeadcountReductionMetricResult,
  type LayersMetricResult,
  type ManagementOverheadMetricResult,
  type ManagerToICRatioMetricResult,
  type SpanMetricResult,
} from "src/util/teamAggregationMetrics";
import {
  toTeamAggregations,
  type TeamAggregations,
} from "src/util/teamAggregations";
import { calculateTeamDepthFromTop } from "src/util/teamMetrics";
import { ENTITY_TYPES } from "src/consts/entityTypes";
import {
  resolveSelectedOrgChartTeam,
  isOrgChartTeam,
} from "./resolveSelectedOrgChartTeam";
import eventEmitter from "src/services/eventEmitter";
import {
  INSIGHTS_STRUCTURE_EVENTS,
  useInsightsHierarchyView,
} from "./useInsightsHierarchyView";
import { useInsightsTargets } from "./useInsightsTargets";
import type { OrgChartState, Team } from "src/components/OrgChart/types";

type GetTeamAggregationsData = {
  aggregations?: { team?: TeamAggregations | null } | null;
};

export type UseSelectedOrgChartTeamResult = {
  currentTeamId: string | null | undefined;
  currentTeam: Team | null;
};

export const useSelectedOrgChartTeam = (
  state: OrgChartState | null | undefined
): UseSelectedOrgChartTeamResult => {
  // Track the last selected team ID separately - only update when a team is selected, not a person.
  const lastTeamIdRef = useRef<string | null>(null);

  const selectedEntityId = state?.expandedPanel?.data?.selectedEntity;
  const selectedEntityType = state?.expandedPanel?.data?.selectedEntityType;
  const isTeamSelected =
    !selectedEntityType || selectedEntityType === ENTITY_TYPES.GROUP;

  const currentTeamId = useMemo(() => {
    if (isTeamSelected && selectedEntityId) {
      return selectedEntityId;
    }
    return lastTeamIdRef.current || state?.currentViewTeam?.id;
  }, [isTeamSelected, selectedEntityId, state?.currentViewTeam?.id]);

  useEffect(() => {
    if (isTeamSelected && selectedEntityId) {
      lastTeamIdRef.current = selectedEntityId;
      return;
    }
    if (!lastTeamIdRef.current && state?.currentViewTeam?.id) {
      lastTeamIdRef.current = state.currentViewTeam.id;
    }
  }, [isTeamSelected, selectedEntityId, state?.currentViewTeam?.id]);

  const currentTeam = useMemo((): Team | null => {
    const resolved = resolveSelectedOrgChartTeam(state, currentTeamId);
    if (isOrgChartTeam(resolved)) {
      return resolved;
    }
    // Only use the view root when it is the selected/tracked team — never
    // pretend a different selected card is the view root (breaks supply gate).
    if (!currentTeamId || state?.currentViewTeam?.id === currentTeamId) {
      return state?.currentViewTeam ?? null;
    }
    return null;
  }, [currentTeamId, state]);

  return { currentTeamId, currentTeam };
};

export type UseTeamMetricsResult = {
  metrics: {
    layers: LayersMetricResult;
    span: SpanMetricResult;
    managerToICRatio: ManagerToICRatioMetricResult;
    managementOverhead: ManagementOverheadMetricResult;
    headcountReduction: HeadcountReductionMetricResult;
  };
  loading: boolean;
  hasTeam: boolean;
  currentTeam: UseSelectedOrgChartTeamResult["currentTeam"];
  aggregations: TeamAggregations;
  hierarchyView: string | null;
  setHierarchyView: (view: string) => void;
  lensSwitchingEnabled: boolean;
};

/**
 * Custom hook to fetch and calculate team metrics for org chart
 * Can be used in Org Charts, Team Details pages, and other components that need team metrics
 * Note: Currently requires OrgChartContext. Can be extended to support other contexts in the future.
 */
export const useTeamMetrics = (
  scenario = "standard",
  { showPositions = false }: { showPositions?: boolean } = {}
): UseTeamMetricsResult => {
  const { state } = useOrgChartContext();
  const dateSettings = useDateSettings();
  const { currentTeamId, currentTeam } = useSelectedOrgChartTeam(state);
  const { targetsByScenario } = useInsightsTargets();
  const {
    hierarchyView,
    setHierarchyView,
    hierarchyReady,
    lensSwitchingEnabled,
  } = useInsightsHierarchyView();
  const teamDepthFromTop = useMemo(
    () => calculateTeamDepthFromTop(currentTeam),
    [currentTeam]
  );

  const variables = useMemo(() => {
    const { toDate } = dateSettings;
    return {
      teamId: currentTeamId,
      // clone: Moment#utc() mutates shared dateSettings.toDate
      pit: toDate ? toDate.clone().utc().valueOf() : null,
      withPositions: showPositions,
    };
  }, [dateSettings, currentTeamId, showPositions]);

  const {
    data: aggregationsData,
    loading: aggregationsLoading,
    refetch: _refetch,
  } = useQuery<GetTeamAggregationsData>(getTeamAggregations, {
    variables,
    fetchPolicy: "cache-and-network",
    errorPolicy: "ignore",
    skip: !currentTeamId,
    notifyOnNetworkStatusChange: true,
  });

  const refetch = useStableCallback(_refetch);

  // ponytail: do not also refetch() when variables change — useQuery already
  // starts a new request, and the extra refetch aborts it (cancelled GQL flood
  // + empty aggregations / N/A on slower tenants like yube).

  // Owner / supply-structure changes: live supply reporting is derived at read
  // time, so aggregations must refetch after ownership or team create/close.
  useEffect(() => {
    if (!currentTeamId || !refetch) {
      return undefined;
    }
    const handler = () => {
      refetch().catch(() => {});
    };
    INSIGHTS_STRUCTURE_EVENTS.forEach((eventName) => {
      eventEmitter.on(eventName, handler);
    });
    return () => {
      INSIGHTS_STRUCTURE_EVENTS.forEach((eventName) => {
        eventEmitter.off(eventName, handler);
      });
    };
  }, [currentTeamId, refetch]);

  const aggregations = useMemo((): TeamAggregations => {
    return toTeamAggregations(aggregationsData?.aggregations?.team);
  }, [aggregationsData]);

  const lensAggregations = useMemo(
    () => selectLensView(aggregations, hierarchyView),
    [aggregations, hierarchyView]
  );
  const viewAggregations = useMemo(
    () =>
      showPositions ? selectPositionView(lensAggregations) : lensAggregations,
    [showPositions, lensAggregations]
  );

  const layersMetric = useMemo(
    () =>
      calculateLayersMetric(viewAggregations, scenario, targetsByScenario, {
        hierarchyView,
        teamDepthFromTop,
      }),
    [
      viewAggregations,
      scenario,
      targetsByScenario,
      hierarchyView,
      teamDepthFromTop,
    ]
  );
  const spanMetric = useMemo(
    () => calculateSpanMetric(viewAggregations, scenario, targetsByScenario),
    [viewAggregations, scenario, targetsByScenario]
  );
  const managerToICRatioMetric = useMemo(
    () =>
      calculateManagerToICRatioMetric(
        viewAggregations,
        scenario,
        targetsByScenario
      ),
    [viewAggregations, scenario, targetsByScenario]
  );
  const managementOverheadMetric = useMemo(
    () =>
      calculateManagementOverheadMetric(
        viewAggregations,
        scenario,
        targetsByScenario
      ),
    [viewAggregations, scenario, targetsByScenario]
  );
  const headcountReductionMetric = useMemo(
    () =>
      calculateHeadcountReductionMetric(
        viewAggregations,
        scenario,
        targetsByScenario
      ),
    [viewAggregations, scenario, targetsByScenario]
  );

  return {
    metrics: {
      layers: layersMetric,
      span: spanMetric,
      managerToICRatio: managerToICRatioMetric,
      managementOverhead: managementOverheadMetric,
      headcountReduction: headcountReductionMetric,
    },
    // Metrics are lens-dependent (selectLensView) — don't paint with the
    // supply-passthrough fallback before the workspace's real default lens
    // (e.g. reporting_line) resolves.
    loading: aggregationsLoading || !hierarchyReady,
    hasTeam: !!currentTeamId,
    currentTeam,
    aggregations,
    hierarchyView,
    setHierarchyView,
    lensSwitchingEnabled,
  };
};
